// THE CHAT'S OPERATIONS, RUN ON THE SERVER.
//
// Daily Tasks, Follow-Up and Initial outreach used to run here, in the tab, as
// a graph of passes (lib/agentRun.js). They now run as three Step Functions
// machines in the terraform repo — draft-task-emails, generate-follow-ups,
// generate-outreach — one Lambda per pass, the emails written by the machine's own write-email
// passes under the same rules as POST /api/enrich/draft. This module is the
// chat's half: it starts the run (POST /api/enrich/operation-start), watches it
// pass by pass (GET /api/enrich/operation-run), and when it finishes hands
// lib/runHost.js the SAME result object the browser engine returns, so the
// receipt, the ledger tile and the draft resolver see no difference.
//
// THE BROWSER ENGINE STAYS AS THE FALLBACK. A run that cannot be started on
// the server — the routes not deployed yet, the machines not configured, the
// API unreachable — returns null here and runHost runs the operation in the
// tab exactly as before. Once a server run HAS started, its outcome is the
// answer: a failed execution is reported as failed, never silently re-run in
// the tab (that would be two runs over the same leads).
//
// WHAT THE SERVER LEAVES TO THE CHAT, ON PURPOSE:
//   • the de-AI-tell pass (outreachDraft.js#humanizeDraft) every model draft
//     gets after it arrives — run here on each server-written email, recorded
//     on its exec as the browser step after the passes, exactly as the
//     resolver records it on a draft it fetched itself;
//   • any email the machine could not write (OpenAI down, a request the draft
//     route would refuse): it arrives `draftPending`, and the receipt's own
//     resolver writes it the way it always has — the model again, then the
//     labelled template.
import { startOperationRun, operationRun, stopOperationRun } from './enrich.js'
import { readExclusions } from './taskExclusions.js'
import { humanizeDraft } from './outreachDraft.js'
import { readPin, savePin } from './batchPin.js'

export const SERVER_OPERATION_IDS = new Set(['followup-50', 'reply-open-threads', 'initial-outreach-50'])

// THE PINNED BATCH CROSSES OVER (lib/batchPin.js — same leads, same order,
// until each is done or reached out to). The tab kept Initial outreach's pin
// in this browser; the machine keeps its own (row _PIN) from its first cut on.
// So a start sends this browser's pin along — the machine carries it only
// while it holds none of its own — and a finished run's pin is copied back
// here, so a run this tab has to make itself carries the same list.
const PINNED_OPERATION_ID = 'initial-outreach-50'

// How often a live run is read, and how long a run may go unread before the
// chat gives up on it (the execution itself carries on and files its receipt;
// only this tab stops waiting).
const POLL_MS = 2500
const READ_FAILURE_GIVE_UP_MS = 90_000
const RUN_GIVE_UP_MS = {
  'followup-50': 20 * 60_000,
  'reply-open-threads': 20 * 60_000,
  'initial-outreach-50': 45 * 60_000,
}

// Each machine's run-level passes, in the order the machine runs them — what
// the chat paints greyed before the first report lands. A report the server
// files under an id not listed here is shown where it arrives.
const SERVER_PASSES = {
  'followup-50': [
    ['org', 'Read who we are', 'The team roster, the CRM owner list and the requester — the addresses and domains this run refuses to write to.'],
    ['identity', 'Find the rep', 'The roster’s rep, resolved to a HubSpot owner by e-mail, then first name — never the signed-in session.'],
    ['tasks', 'Read the HubSpot tasks', 'The rep’s open tasks from the mirror — overdue, or due today on the Chicago calendar — in the rep’s own wording.'],
    ['history', 'Read the mail history', 'Each address’s real conversation, read once for the whole run.'],
    ['users', 'Read customer usage', 'The portal’s product-usage attributes in HubSpot.'],
    ['context', 'Read each task’s conversation', 'Per company: who to write to, the real thread, the rep’s notes, open deals and product usage.'],
    ['drafts', 'Write the emails', 'One email per task, drafted on the server under the same rules as POST /api/enrich/draft.'],
    ['briefing', 'Assemble the emails', 'One email card per task. Nothing is created or sent.'],
  ],
  'reply-open-threads': [
    ['org', 'Read who we are', 'The team roster, the CRM owner list and the requester.'],
    ['users', 'Read customer usage', 'The portal’s product-usage attributes in HubSpot.'],
    ['history', 'Read the mail history', 'Each address’s real conversation, read once for the whole run.'],
    ['customers', 'Build the four lanes', 'Inactive users, trials ending within 7 days, expired trials and failed payments.'],
    ['drafts', 'Write the emails', 'One check-in per inactive user, drafted on the server.'],
    ['briefing', 'Assemble the follow-up', 'Four sections, every card wearing its status — nothing created, nothing sent.'],
  ],
  'initial-outreach-50': [
    ['org', 'Read who we are', 'The team roster, the CRM owner list and the requester.'],
    ['source', 'Select candidates', 'Every masonry lead in the directory, deduped and ranked by score.'],
    ['reachable', 'Filter to reachable', 'A lead with no email address is not a lead we can contact.'],
    ['history', 'Read the mail history', 'Each address’s real conversation, read once for the whole run.'],
    ['contacted', 'Exclude contacted companies', 'New means nobody at the company yet.'],
    ['hs-match', 'Open the HubSpot mirror', 'One read of the match index — the CRM is read here, never written.'],
    ['hs-deals', 'Check open deals', 'Is any of these leads already inside a live deal?'],
    ['hs-tasks', 'Check open tasks', 'Open CRM tasks somebody already owns on these companies.'],
    ['hs-screen', 'Apply the CRM screen', 'Anyone in an open deal is out; everything else carries forward.'],
    ['pin', 'Pin the batch', 'Same leads, same order, until each is done or reached out.'],
    ['enriched', 'Research the leads', 'Cached dossiers are free, fresh ones spend, all under a time budget.'],
    ['drafts', 'Write the emails', 'One intro email per lead, drafted on the server.'],
  ],
}

// The Map's pass, per machine: while the branches write, its card counts them.
const BRANCH_PASS = { 'followup-50': 'context', 'reply-open-threads': 'drafts', 'initial-outreach-50': 'enriched' }

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms)
    signal?.addEventListener?.('abort', () => {
      clearTimeout(t)
      reject(abortError())
    })
  })

function abortError() {
  const err = new Error('The run was stopped')
  err.name = 'AbortError'
  return err
}

function placeholder(id, title, subtitle, dependsOn) {
  return {
    id,
    title,
    subtitle,
    dependsOn,
    lane: null,
    status: 'pending',
    headline: '',
    detail: [],
    metric: null,
    startedAt: null,
    endedAt: null,
  }
}

/** The machine's run-level passes, all pending — painted before the run starts. */
export function serverInitialPasses(operationId) {
  const list = SERVER_PASSES[operationId] || []
  return list.map(([id, title, subtitle], i) => placeholder(id, title, subtitle, i ? [list[i - 1][0]] : []))
}

/**
 * The live pass list: every report the server filed, in its order, with the
 * still-pending placeholders after them, and the first pending one marked
 * running — the machine is a chain, so whatever has not reported yet after
 * the last report is what is working now. The branch pass counts its items.
 */
export function livePasses(operationId, reports, progress, batchSize) {
  const filed = (Array.isArray(reports) ? reports : []).filter((p) => p && p.id)
  const seen = new Set(filed.map((p) => p.id))
  const pending = serverInitialPasses(operationId).filter((p) => !seen.has(p.id))
  const out = [...filed.map((p) => ({ dependsOn: [], lane: null, ...p })), ...pending]
  const next = out.findIndex((p) => p.status === 'pending')
  if (next >= 0) {
    const working = { ...out[next], status: 'running', startedAt: Date.now() }
    if (working.id === BRANCH_PASS[operationId] && batchSize > 0) {
      const stored = Number(progress?.stages?.['store-email']) || 0
      working.headline = `${stored.toLocaleString('en-US')} of ${batchSize.toLocaleString('en-US')} written so far`
    }
    out[next] = working
  }
  return out
}

/**
 * The browser step after the passes, on every email the machine wrote: the
 * same de-AI-tell pass the resolver runs on a draft it fetched itself, with
 * the same record of it on the exec. Refusals and drafts that already carry a
 * browser step are left exactly as they are.
 */
export function finishServerDrafts(items) {
  return (Array.isArray(items) ? items : []).map((item) => {
    const draft = item?.draft
    if (!draft || typeof draft !== 'object' || draft.skipped) return item
    if (!draft.bodyText || draft.exec?.post) return item
    const built = humanizeDraft({
      subject: draft.subject,
      bodyText: draft.bodyText,
      sources: Array.isArray(draft.sources) ? draft.sources : [],
    })
    return {
      ...item,
      draft: {
        ...draft,
        subject: built.subject,
        bodyText: built.bodyText,
        ...(built.report ? { report: built.report } : {}),
        ...(built.sources?.length ? { sources: built.sources } : {}),
        exec: {
          ...(draft.exec || { runtime: 'aws-lambda' }),
          post: [{ runtime: 'browser', file: 'src/lib/outreachDraft.js', calls: ['humanizeDraft'] }],
        },
      },
    }
  })
}

// The receipt slots that hold drafted items, per operation — every one gets
// the browser step above.
const DRAFTED_SLOTS = ['tasks', 'inactiveUsers', 'selected']

/**
 * Run one operation on the server. Resolves the browser engine's result
 * object once the run has finished; resolves NULL when the run could not be
 * started on the server at all (the caller then runs it in the tab); throws
 * when a run that did start failed, and AbortError when the caller stopped it.
 *
 * @param {object} opts
 * @param {string} opts.operationId   one of SERVER_OPERATION_IDS
 * @param {string} [opts.category]    the directory category (initial outreach)
 * @param {string} [opts.scopeCode]   the tile's scope (initial outreach)
 * @param {AbortSignal} [opts.signal] the run's Stop
 * @param {function} [opts.onPasses]  (passes) on every read of a live run
 * @param {function} [opts.onStarted] ({ runId }) once the execution exists
 */
export async function runOperationOnServer({ operationId, category = '', scopeCode = '', signal, onPasses, onStarted } = {}) {
  if (!SERVER_OPERATION_IDS.has(operationId)) return null
  if (signal?.aborted) throw abortError()

  // The Settings list lives in this browser; it travels only when the rep set
  // one, and the server runs on the same defaults when it does not.
  const stored = readExclusions()
  const pinned = operationId === PINNED_OPERATION_ID && category ? readPin(operationId, category).keys : []
  const body = {
    operationId,
    ...(category ? { category } : {}),
    ...(scopeCode ? { scope: scopeCode } : {}),
    ...(stored && stored.isDefault === false ? { exclusions: stored.entries } : {}),
    ...(pinned.length ? { pin: pinned } : {}),
  }
  let started = null
  try {
    started = await startOperationRun(body, { signal })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    // Not started on the server: not deployed (404), not reachable, or the
    // session cannot ask (401/403). The tab runs it, as it always has.
    return null
  }
  if (!started?.ok || !started.runId) return null
  const runId = started.runId
  onStarted?.({ runId, machine: started.machine })
  onPasses?.(livePasses(operationId, [], null, 0))

  const giveUpAt = Date.now() + (RUN_GIVE_UP_MS[operationId] || 20 * 60_000)
  let lastRead = Date.now()
  for (;;) {
    if (signal?.aborted) {
      stopOperationRun(runId).catch(() => {})
      throw abortError()
    }
    let read = null
    try {
      read = await operationRun(runId, { signal })
      lastRead = Date.now()
    } catch (err) {
      if (err?.name === 'AbortError') {
        stopOperationRun(runId).catch(() => {})
        throw err
      }
      if (Date.now() - lastRead > READ_FAILURE_GIVE_UP_MS) {
        throw new Error(`The server run ${runId} could not be read for over a minute — it may still finish; its receipt is filed with the run.`)
      }
    }
    if (read) {
      const status = String(read.status || '')
      if (status === 'SUCCEEDED' && read.receipt) {
        const result = { ...read.receipt }
        for (const slot of DRAFTED_SLOTS) {
          if (Array.isArray(result[slot])) result[slot] = finishServerDrafts(result[slot])
        }
        result.passes = Array.isArray(read.passes) ? read.passes : result.passes || []
        result.serverRun = { runId, machine: read.machine || started.machine, finishedAt: read.run?.finishedAt || '' }
        if (operationId === PINNED_OPERATION_ID && category && Array.isArray(read.pin?.keys)) {
          savePin(operationId, category, read.pin.keys)
        }
        onPasses?.(result.passes)
        return result
      }
      if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED') {
        onPasses?.(livePasses(operationId, read.passes, read.progress, 0))
        throw new Error(read.error || `The server run ${status.toLowerCase().replace('_', ' ')}`)
      }
      onPasses?.(livePasses(operationId, read.passes, read.progress, Number(read.run?.batchSize) || 0))
    }
    if (Date.now() > giveUpAt) {
      throw new Error(`The server run ${runId} is taking longer than it should — it may still finish; its receipt is filed with the run.`)
    }
    try {
      await sleep(POLL_MS, signal)
    } catch (err) {
      // Stop pressed between two reads: the execution is stopped too, so a
      // run nobody is waiting for does not go on spending.
      stopOperationRun(runId).catch(() => {})
      throw err
    }
  }
}
